import type { PostingIdentity } from "../../lib/api";
import { projects as mockProjects } from "../../data/mock";

export const TABS = ["Project Details", "Attachments", "Bidding", "Q&A"];

export const CAN_BID = ["offer_service", "promote_company"];

export const ATTACHMENT_PREVIEW_COUNT = 5;

// Bid durations are free text. Put unparseable values last when the owner sorts
// by shortest, rather than pretending an unknown duration is fast.
export function durationInDays(value: string | null | undefined): number {
  const match = value?.trim().match(/^(\d+(?:\.\d+)?)\s*(days?|weeks?|months?|years?)\.?$/i);
  if (!match) return Number.POSITIVE_INFINITY;
  const amount = Number(match[1]);
  const unit = match[2].toLowerCase();
  return amount * (unit.startsWith("week") ? 7 : unit.startsWith("month") ? 30 : unit.startsWith("year") ? 365 : 1);
}

export type BidDocument = { id: string; filename: string; size: number; contentType: string; createdAt: string };

export type ViewModel = {
  postingIdentity?: PostingIdentity;
  id: string; title: string; category: string; location: string; budget: number;
  budgetType: string; deadline: string; targetCompletionDate: string | null; bids: number; owner: string; ownerSince: string; posted: string;
  duration: string; paymentTerms: string; description: string; previousWork: string; status: string;
  scope: string[]; requirements: string[]; licenses: string[]; attachments: number;
  timeline: { label: string; date: string }[]; ownerUserId: number | null; companyId?: string | null;
  clientSummary: { name: string; profileImageUrl: string | null; memberSince: string; projectsPosted: number; projectsAwarded: number; rating: number | null; reviewCount: number; identityVerified: boolean; verifiedProfessions?: string[] } | null;
};

export type ProjectQuestionMessage = {
  id: string;
  senderUserId: number;
  senderName: string;
  message: string;
  createdAt: string;
};

export type BidCompany = { id: string; name: string };

export const asArr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

// Bundled demo projects may lack API details; prefer live fields but keep
// their seeded fallback so existing demo links remain readable.
export function buildModel(id: string, apiItem: any | null): ViewModel | null {
  const mock = mockProjects.find((x) => x.id === id) || null;
  if (!apiItem && !mock) return null;
  const detail = (apiItem?.detail && typeof apiItem.detail === "object") ? apiItem.detail : {};
  const pick = <T,>(a: T | undefined | null, b: T | undefined | null, d: T): T =>
    (a ?? b ?? d) as T;
  return {
    id: apiItem?.id || mock?.id || id,
    title: pick(apiItem?.title, mock?.title, "Project"),
    category: pick(apiItem?.category, mock?.category, "General"),
    location: apiItem?.location === "Philippines" && mock ? mock.location : pick(apiItem?.location, mock?.location, "Philippines"),
    // A redacted private estimate must never fall back to a public mock amount.
    budget: Number(apiItem?.budget ?? 0),
    budgetType: pick(apiItem?.budgetType, mock?.budgetType, "Estimated"),
    deadline: pick(apiItem?.deadline, mock?.deadline, "Open"),
    targetCompletionDate: typeof detail.targetCompletionDate === "string" ? detail.targetCompletionDate : null,
    bids: Number(pick(apiItem?.bids, mock?.bids, 0)),
    owner: pick(apiItem?.owner, mock?.owner, "KontrataPH Member"),
    ownerSince: pick(detail.ownerSince, mock?.ownerSince, "2025"),
    posted: apiItem?.createdAt ? new Date(apiItem.createdAt).toLocaleDateString("en-PH", { dateStyle: "medium" }) : pick(mock?.posted, null, ""),
    duration: pick(detail.duration || null, mock?.duration, "To be discussed"),
    paymentTerms: pick(detail.paymentTerms || null, mock?.paymentTerms, "Milestone-based"),
    description: pick(detail.description || null, mock?.description, "No description provided."),
    previousWork: (typeof detail.previousWork === "string" ? detail.previousWork : "") || "",
    status: pick(apiItem?.status, "open", "open"),
    scope: asArr(detail.scope).length ? asArr(detail.scope) : (mock?.scope || []),
    requirements: asArr(detail.requirements).length ? asArr(detail.requirements) : (mock?.requirements || []),
    licenses: asArr(detail.licenses).length ? asArr(detail.licenses) : (mock?.licenses || []),
    attachments: mock?.attachments ?? 0,
    timeline: !apiItem && mock?.timeline ? mock.timeline : [
      { label: "Project Posted", date: apiItem?.createdAt ? new Date(apiItem.createdAt).toLocaleDateString() : "—" },
      { label: "Open for Bidding", date: `Until ${pick(apiItem?.deadline, mock?.deadline, "the deadline")}` },
      { label: "Shortlisting", date: "After the deadline" },
      { label: "Awarding", date: "TBD" },
      ...(typeof detail.targetCompletionDate === "string" ? [{ label: "Client target completion", date: detail.targetCompletionDate }] : []),
    ],
    ownerUserId: apiItem?.ownerUserId ?? null,
    companyId: apiItem?.companyId ?? detail.companyId ?? null,
    clientSummary: apiItem?.clientSummary ?? null,
    postingIdentity: apiItem?.postingIdentity,
  };
}

export const money = (s: string) => Number(String(s).replace(/[^\d]/g, "")) || 0;

export type ProjectLoadState = "loading" | "ready" | "notfound" | "unavailable";

export const getErrorStatus = (error: unknown): number | null => {
  if (typeof error !== "object" || error === null || !("status" in error)) return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
};

export const fmtSize = (n: number) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
